import DirectionForward from '@clearkit/icons/glyphs/DirectionForward';
import EnrichmentBlock from '@clearkit/icons/glyphs/EnrichmentBlock';
import RestartIcon from '@clearkit/icons/glyphs/Restart';
import Trash from '@clearkit/icons/glyphs/Trash';
import Clearbit from '@clearkit/icons/services/Clearbit';
import HubSpot from '@clearkit/icons/services/Hubspot';
import Marketo from '@clearkit/icons/services/Marketo';
import Salesforce from '@clearkit/icons/services/Salesforce';
import { CKAttributeSelector, CKButton } from 'clearkit';
import { useState } from 'react';

import { isTypeMismatch } from '~/lib/isTypeMismatch';
import { findAttribute } from '~lib/dataEnrichment';

export const RefreshButton = ({ onClickRefresh }) => {
  const [loading, setLoading] = useState(false);
  const onClick = async () => {
    setLoading(true);

    try {
      await onClickRefresh();
    } catch (error) {
      console.error(error); // eslint-disable-line no-console
    } finally {
      setLoading(false);
    }
  };

  return (
    <CKButton
      isDisabled={loading}
      leftIcon={<RestartIcon />}
      onClick={onClick}
      variant="tertiary"
      variantColor="blue"
    >
      {loading ? 'Refreshing' : 'Refresh'}
    </CKButton>
  );
};

export default function FormFieldMappingRow({
  mapping,
  service,
  onChange,
  onRemove,
  sourceAttributes,
  targetAttributes,
  onClickRefreshAttributes,
  sourceErrors,
  targetErrors,
  showError,
}) {
  const mappingToAttribute = (mappingValue) => {
    if (!mappingValue) return null;

    const [sourceType, objectType, ...value] = mappingValue.split('.');

    return {
      sourceType,
      objectType,
      value: value.join('.'),
    };
  };

  const mappingAttributeToValue = (mappingAttribute) => {
    const heirarchy = [];
    heirarchy.push(mappingAttribute.meta.sourceType);
    heirarchy.push(mappingAttribute.meta.objectType);
    heirarchy.push(mappingAttribute.value);
    return heirarchy.join('.');
  };

  const selectedSourceAttribute = sourceAttributes
    ? findAttribute(
        mappingToAttribute(mapping.sourceAttribute),
        sourceAttributes,
      )
    : null;
  const selectedTargetAttribute = targetAttributes
    ? findAttribute(
        mappingToAttribute(mapping.targetAttribute),
        targetAttributes,
      )
    : null;

  const inErrorState = (sourceOrTarget) => {
    if (sourceOrTarget === 'source') {
      return !!sourceErrors || (!mapping.sourceAttribute && showError);
    } else if (sourceOrTarget === 'target') {
      return !!targetErrors || (!mapping.targetAttribute && showError);
    } else return false;
  };

  const renderServiceIcon = (service) => {
    switch (service) {
      case 'HubSpot':
        return <HubSpot height={18} width={18} />;
      case 'Marketo':
        return <Marketo height={18} width={18} />;
      case 'Pardot':
        return <Salesforce height={18} width={18} />;
      default:
        return <EnrichmentBlock height={18} width={18} />;
    }
  };

  const rootCategoryIdentifier = (service) => {
    switch (service) {
      case 'HubSpot':
        return 'category_hubspot';
      case 'Marketo':
        return 'category_marketo';
      case 'Pardot':
        return 'category_pardot';
      default:
        return 'category_enrichment';
    }
  };

  return (
    <div className="forms-mapping-row-grid">
      <CKAttributeSelector
        attributes={sourceAttributes || null}
        icon={<Clearbit height={18} width={18} />}
        isTypeMismatch={(attribute) =>
          isTypeMismatch(attribute, selectedTargetAttribute)
        }
        onSelectedItemChange={(selected) =>
          onChange({
            sourceAttribute: mappingAttributeToValue(selected),
          })
        }
        placeholder="Find an attribute"
        selectedAttribute={selectedSourceAttribute || {}}
        variant={inErrorState('source') && 'error'}
        zIndex={30}
      />
      <DirectionForward className="text-gray-400" />
      <CKAttributeSelector
        attributes={targetAttributes || null}
        detailDrawerPosition="left"
        icon={renderServiceIcon(service)}
        isTypeMismatch={(attribute) =>
          isTypeMismatch(selectedSourceAttribute, attribute)
        }
        onSelectedItemChange={(selected) =>
          onChange({
            targetAttribute: mappingAttributeToValue(selected),
          })
        }
        placeholder={`Choose a ${service} attribute`}
        rootCategoryIdentifier={rootCategoryIdentifier(service)}
        rootCategoryLabel={service}
        rootCategoryMeta={
          <RefreshButton onClickRefresh={onClickRefreshAttributes} />
        }
        selectedAttribute={selectedTargetAttribute || {}}
        variant={inErrorState('target') && 'error'}
        zIndex={30}
      />
      <CKButton isSmall onClick={onRemove} variant="tertiary">
        <Trash className="text-gray-400" />
      </CKButton>
    </div>
  );
}
